import { useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

const useBlobUrl = (id) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u = null;
    let alive = true;
    api.get(`/attachments/${id}/download`, { responseType: "blob" }).then(({ data }) => {
      u = URL.createObjectURL(data);
      if (alive) setUrl(u); else URL.revokeObjectURL(u);
    }).catch(() => alive && setUrl(""));
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [id]);
  return url;
};

export const AttachmentThumb = ({ a, canDelete, onDelete }) => {
  const { t, fmt } = useI18n();
  const url = useBlobUrl(a.id);
  const [open, setOpen] = useState(false);
  const download = () => { const l = document.createElement("a"); l.href = url; l.download = a.original_filename; l.click(); };

  return (
    <>
      <div className="relative">
      <button type="button" onClick={() => url && setOpen(true)} data-testid={`attachment-thumb-${a.id}`}
        className="group relative aspect-square rounded-md overflow-hidden border bg-slate-100 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-500">
        {url ? <img src={url} alt={a.original_filename} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
          : <div className="w-full h-full animate-pulse bg-slate-200" />}
        <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] px-1.5 py-0.5 truncate text-start" dir="auto">{a.original_filename}</span>
      </button>
      {canDelete && (
        <button type="button" onClick={() => onDelete(a)} data-testid={`attachment-thumb-delete-${a.id}`} title={t("delete")}
          className="absolute top-1 start-1 flex items-center gap-1 rounded-md bg-rose-600/90 hover:bg-rose-700 text-white text-[11px] font-bold px-1.5 py-1 shadow transition-colors">
          <Trash2 className="w-3.5 h-3.5" />{t("delete")}
        </button>
      )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-3" data-testid="attachment-lightbox">
          <DialogTitle className="text-sm font-bold truncate pe-8" dir="auto">{a.original_filename}</DialogTitle>
          <DialogDescription className="text-xs">{a.uploaded_by} · {fmt(a.created_at)}</DialogDescription>
          {url && <img src={url} alt={a.original_filename} className="w-full max-h-[70vh] object-contain rounded bg-slate-50" data-testid="attachment-lightbox-img" />}
          <div className="flex justify-end gap-2">
            {canDelete && <Button variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid={`attachment-delete-${a.id}`} onClick={() => { setOpen(false); onDelete(a); }}><Trash2 className="w-4 h-4" />{t("delete")}</Button>}
            <Button variant="outline" data-testid={`attachment-download-${a.id}`} onClick={download}><Download className="w-4 h-4" />{t("download")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
